/**
 * The website agent's booking view (FLO-618): a header strip and either the
 * allowlisted provider iframe or an "open it where it works" fallback.
 *
 * ONE component with two homes:
 *   - inside the chat panel, as a VIEW that replaces the conversation body;
 *   - as a STAGE beside the presenter (the live dock, or the resting tile after
 *     a call), in the geometry the live demo uses.
 *
 * It owns the iframe ref and the provider hint listener so both homes report
 * booking UX hints identically — a second copy of that listener is how one of
 * them ends up stale.
 */
import React, { useEffect, useRef } from 'react';
import { getString } from '../sdk/ui-strings';
import type { CalendarEmbed } from '../sdk/calendar-embed';
import { bookingUxHintFromMessage, type BookingUxHint } from '../sdk/booking-ux-hint';

const C = {
  surface: '#FFFFFF',
  body: '#FBF9F8',
  ink: '#1C1917',
  sub: '#79716B',
  line: '#EEEBE8',
};

export const BookingSurface: React.FC<{
  calendar: CalendarEmbed;
  /** The booking label — header title and fallback CTA. */
  title: string;
  accent: string;
  onClose?: () => void;
  onBookingUxHint?: (hint: BookingUxHint) => void;
  /**
   * Where the close control points. A view inside the chat goes BACK to the
   * conversation (chevron, "Back to the conversation"); a stage simply closes
   * (cross, "Close") — during a call the conversation never went anywhere, and
   * after End there is none to go back to.
   */
  closeKind?: 'back' | 'close';
}> = ({ calendar, title, accent, onClose, onBookingUxHint, closeKind = 'back' }) => {
  const frameRef = useRef<HTMLIFrameElement>(null);
  const hintSentForUrl = useRef<string | null>(null);

  useEffect(() => {
    if (!calendar.embedSrc || !onBookingUxHint) return;
    const onMessage = (event: MessageEvent) => {
      const hint = bookingUxHintFromMessage({
        event,
        iframe: frameRef.current,
        calendarUrl: calendar.embedSrc,
      });
      if (!hint || hintSentForUrl.current === calendar.embedSrc) return;
      hintSentForUrl.current = calendar.embedSrc;
      onBookingUxHint(hint);
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [calendar.embedSrc, onBookingUxHint]);

  // "Back to the conversation" would be false on a stage opened after End, so
  // the cross has its own key (a ui-strings contract change with floe-api).
  const closeLabel =
    closeKind === 'back' ? getString('wa.booking.back') : getString('wa.booking.close');

  return (
    <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', background: C.body }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 10px 9px 8px', borderBottom: `1px solid ${C.line}`, background: C.surface, flexShrink: 0 }}>
        {onClose && closeKind === 'back' && (
          <button type="button" onClick={onClose} className="floe-wac-ib" aria-label={closeLabel} style={iconBtn(C.sub)}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="15 18 9 12 15 6" /></svg>
          </button>
        )}
        <span style={{ fontSize: 13.5, fontWeight: 650, letterSpacing: '-0.01em', color: C.ink, paddingLeft: closeKind === 'close' ? 6 : 0 }}>{title}</span>
        {calendar.fallbackHref && (
          <a href={calendar.fallbackHref} target="_blank" rel="noopener noreferrer"
            style={{ marginLeft: 'auto', fontSize: 12, fontWeight: 600, color: accent, textDecoration: 'none', padding: '5px 8px' }}>
            {getString('wa.booking.openNewTab')}
          </a>
        )}
        {onClose && closeKind === 'close' && (
          <button type="button" onClick={onClose} className="floe-wac-ib" aria-label={closeLabel} title={closeLabel} style={iconBtn(C.sub)}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12" /></svg>
          </button>
        )}
      </div>
      {calendar.embedSrc ? (
        <iframe
          ref={frameRef}
          src={calendar.embedSrc}
          title={title}
          style={{ flex: 1, minHeight: 0, width: '100%', border: 'none', display: 'block', background: C.body }}
          /* clipboard-write covers Cal.com's "copy link" affordances; no
             camera/mic here — unlike the demo's full-bleed embed this sits
             beside a LIVE mic session, and handing a third-party frame the
             same devices the agent is using is not something a booking page
             needs. */
          allow="clipboard-write"
          loading="lazy"
          referrerPolicy="no-referrer"
        />
      ) : (
        /* Configured, https, but not a host we will frame (HubSpot, Chili
           Piper, a self-hosted booker…). The offer still stands — it just
           opens where it works. */
        <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 14, padding: '24px 28px', textAlign: 'center' }}>
          <div style={{ fontSize: 15, fontWeight: 650, letterSpacing: '-0.01em', color: C.ink }}>{getString('wa.booking.title')}</div>
          <div style={{ fontSize: 13.5, color: C.sub, maxWidth: 260 }}>
            {getString('wa.booking.body')}
          </div>
          <a href={calendar.fallbackHref!} target="_blank" rel="noopener noreferrer" className="floe-wac-cta"
            style={{ marginTop: 2, padding: '11px 22px', borderRadius: 999, background: accent, color: '#fff', fontSize: 14, fontWeight: 600, textDecoration: 'none', boxShadow: '0 6px 18px -6px rgba(0,0,0,0.35)' }}>
            {title}
          </a>
        </div>
      )}
    </div>
  );
};

function iconBtn(color: string): React.CSSProperties {
  return {
    width: 34, height: 34, borderRadius: '50%', border: 'none', background: 'transparent',
    color, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
  };
}
